// Material application (DECISION-053 Slice-2) — THREE-using, shared by the shell path
// (template-composition) AND the fixture path (engine/composer). Builds a real PBR material from
// a resolved library map-set, projects it onto a mesh with per-FACE world-axis box mapping
// (the triplanar upgrade — fixes planar streaks on the perpendicular side faces of multi-facing
// meshes like the door + boxes), and applies fixture bindings to a fixture's mesh slots.

import * as THREE from "../../vendor/three.module.js";
import { loadKtx2Map } from "./_gltf-loader-factory.js";
import { resolveAssetKeyUrl } from "./_asset-url-resolver.js";
import { resolveMapSet, resolveTilingMeters, resolveTint, resolveFixtureSlotBindings, resolveHeaderSignageSelection } from "./_material-binding.js";
import { buildSpinnerHeaderTexture } from "./fixtures/_label-helpers.js";   // signage texture generation (reused, not re-ported)

// Build a real PBR material from a resolved library map-set (loose .ktx2). glTF metallic-roughness:
// map=albedo(sRGB), normalMap=normal, and the SHARED orm.ktx2 feeds roughnessMap(G)+metalnessMap(B)+
// aoMap(R). factors multiply the maps; tint multiplies albedo. Maps load through the shared
// GPU-detected KTX2 transcoder (loadKtx2Map), role-configured (colourSpace/flipY/wrap/anisotropy).
export async function buildMaterialFromLibrary(mapSet, binding, { renderer, anisotropy }) {
	const [r, g, b] = resolveTint(mapSet, binding);
	// Tint components are authored sRGB (hex chips) — declare the colourspace so THREE converts to
	// the linear working space. `new Color(r,g,b)` treats them as ALREADY-linear → tints rendered
	// lighter than authored (E2E part-1 caught #88ccee painting as c1e7f7).
	const mat = new THREE.MeshStandardMaterial({
		color: new THREE.Color().setRGB(r, g, b, THREE.SRGBColorSpace),
		metalness: mapSet.factors.metalness,
		roughness: mapSet.factors.roughness,
	});
	// ASK-084 Part-3 tune — metals (metalness→1) render ONLY the environment reflection (no diffuse),
	// so a metalness=1/roughness=1 material reads dark under a dim env. Scale envMapIntensity by
	// metalness: metals reflect brighter (1 + 0.9·metalness), dielectrics (wall/floor/wood, metalness
	// 0) stay at 1.0 — targeted fix, no over-brightening of the shell. (Coord render-truth re-captures.)
	mat.envMapIntensity = 1 + 0.9 * (Number(mapSet.factors.metalness) || 0);
	const opts = { renderer, anisotropy };
	const url = (k) => resolveAssetKeyUrl(k);
	const jobs = [];
	if (mapSet.baseColor) jobs.push(loadKtx2Map(url(mapSet.baseColor), "baseColor", opts).then((t) => { mat.map = t; }));
	if (mapSet.normal)    jobs.push(loadKtx2Map(url(mapSet.normal),    "normal",    opts).then((t) => {
		// loadKtx2Map sets the silent-flatten gotchas: colourSpace linear (NoColorSpace, NOT sRGB)
		// + flipY=false (KTX2/glTF convention). normalScale explicit (>0, regression-proof).
		mat.normalMap = t;
		mat.normalMapType = THREE.TangentSpaceNormalMap;
		mat.normalScale.set(1, 1);
	}));
	if (mapSet.orm)       jobs.push(loadKtx2Map(url(mapSet.orm),       "roughness", opts).then((t) => {
		// One linear ORM texture drives all three (THREE r183 samples G→rough, B→metal, R→ao).
		mat.roughnessMap = t;
		if (mapSet.hasMetalRough) mat.metalnessMap = t;
		if (mapSet.hasAo) mat.aoMap = t;   // aoMap samples the 2nd UV set — projection writes uv1
	}));
	await Promise.all(jobs);
	mat.needsUpdate = true;
	return mat;
}

// Per-FACE world-axis box projection (the triplanar upgrade). Each triangle projects onto the plane
// perpendicular to its dominant WORLD normal, scaled so one tile = `metresPerTile` of world space.
// Fixes the planar-projection streaks on perpendicular side faces (door edges, box sides) that a
// single-axis per-mesh projection produces. De-indexes so each triangle owns its UVs (an axis-
// transition edge needs different UVs per face). Writes uv1 for aoMap. World-baked → static meshes.
// (Axis-aligned shell/box/door geometry is seam-free; a fully blended triplanar SHADER is the
// further upgrade only if a curved surface shows an axis-transition seam.)
export function applyBoxProjectedUVs(mesh, metresPerTile, { withUv1 = false } = {}) {
	let geo = mesh.geometry;
	if (!geo?.getAttribute("position")) return;
	if (geo.index) { geo = geo.toNonIndexed(); mesh.geometry = geo; }
	mesh.updateWorldMatrix(true, false);
	const mw = mesh.matrixWorld;
	const pos = geo.getAttribute("position");
	const tile = Math.max(0.01, metresPerTile);
	const uv = new Float32Array(pos.count * 2);
	const wa = new THREE.Vector3(), wb = new THREE.Vector3(), wc = new THREE.Vector3();
	const ab = new THREE.Vector3(), ac = new THREE.Vector3(), n = new THREE.Vector3();
	for (let t = 0; t + 2 < pos.count; t += 3) {
		wa.set(pos.getX(t),     pos.getY(t),     pos.getZ(t)).applyMatrix4(mw);
		wb.set(pos.getX(t + 1), pos.getY(t + 1), pos.getZ(t + 1)).applyMatrix4(mw);
		wc.set(pos.getX(t + 2), pos.getY(t + 2), pos.getZ(t + 2)).applyMatrix4(mw);
		n.crossVectors(ab.subVectors(wb, wa), ac.subVectors(wc, wa));
		const ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z);
		const plane = (ax >= ay && ax >= az) ? "x" : (ay >= az ? "y" : "z");
		for (let k = 0; k < 3; k++) {
			const w = k === 0 ? wa : (k === 1 ? wb : wc);
			let u, v;
			if (plane === "x")      { u = w.z; v = w.y; }
			else if (plane === "y") { u = w.x; v = w.z; }
			else                    { u = w.x; v = w.y; }
			uv[(t + k) * 2] = u / tile; uv[(t + k) * 2 + 1] = v / tile;
		}
	}
	geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
	if (withUv1) geo.setAttribute("uv1", new THREE.BufferAttribute(uv.slice(), 2));
	geo.attributes.uv.needsUpdate = true;
}

// DECISION-053 Slice-2 — SIGNAGE material (dynamic: shop logo/name), the uniform signage path
// through the material system. Reuses the existing buildSpinnerHeaderTexture generation (canvas
// text) for the "label" source; loads the uploaded logo for "logo". Generalises to SIGN_ANCHOR +
// any future signage surface — the builder is source-agnostic. Unlike tiled library materials,
// signage keeps the mesh's AUTHORED UVs (a decal, not a repeat) and needs no box projection.
export async function buildSignageMaterial({ source, text, logoUrl, stage, anisotropy = 4 }) {
	let map = null;
	if (source === "logo" && logoUrl && stage?.textureLoader) {
		map = await new Promise((resolve) => {
			stage.textureLoader.load(logoUrl,
				(t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = anisotropy; t.flipY = false; t.needsUpdate = true; resolve(t); },
				undefined, () => resolve(null));
		});
	}
	if (!map && text) map = buildSpinnerHeaderTexture(text);   // "label" source, or logo-load fallback
	if (!map) return null;
	const mat = new THREE.MeshStandardMaterial({ map, roughness: 0.6, metalness: 0 });
	mat.needsUpdate = true;
	return mat;
}

/** Apply a signage material to a fixture's signage slot meshes (e.g. spinner-rack "header" → the
 * header_logo_face meshes). Builds via buildSignageMaterial (source-agnostic) and assigns on the
 * meshes' AUTHORED UVs (signage is a decal — NOT box-projected/tiled). Returns faces skinned. */
export async function applyFixtureSignage({ meshes, ids, source, text, logoUrl, stage, anisotropy = 4 }) {
	if (!Array.isArray(meshes) || !Array.isArray(ids) || !ids.length) return 0;
	const material = await buildSignageMaterial({ source, text, logoUrl, stage, anisotropy });
	if (!material) return 0;
	let n = 0;
	for (const mesh of meshesForIds(meshes, ids)) { mesh.material = material; n++; }
	return n;
}

/** Resolve + apply a fixture header's signage with the LAYERED precedence (DECISION-053 Slice-2):
 *   source: per-anchor headerSource knob  >  shop signMode (typographic-shopname→text, uploaded-logo→logo)
 *   text:   per-anchor label override (boxOrientations / extras)  >  shop-name (never-blank fallback)
 *   logo:   shop brand logo
 * The per-anchor layer uses the OVERRIDE only (NOT the fixture default label, which would mask the
 * shop name). Shared by the composer (initial) and recomposeTemplate (live knob hot-swap). */
export async function applyHeaderSignage({ stage, scene, meshes, ids, anchorName, extrasLabel = null }) {
	if (!Array.isArray(meshes) || !Array.isArray(ids) || !ids.length) return 0;
	const brand = stage?._shopBrandSignage || {};
	const knobs = scene?.template?.knobs?.boxOrientations?.[anchorName] || {};
	const brandName = brand.name || scene?.template?.knobs?.shopName || stage?._commerceCached?.storeName || null;
	const brandLogo = brand.logoUrl || stage?._projectAssetsCached?.shopLogo?.url || stage?._projectAssetsCached?.spinnerRack?.headerLogoUrl || null;
	// Layered precedence (pure + gated by test-material-binding): per-anchor override > shop signMode/brand.
	const { source, text } = resolveHeaderSignageSelection({
		headerSource: knobs.headerSource,
		signMode:     brand.signMode,
		anchorLabel:  knobs.label ?? null,
		extrasLabel,
		shopName:     brandName,
	});
	return applyFixtureSignage({ meshes, ids, source, text, logoUrl: brandLogo, stage });
}

/** Collect isMesh descendants of the given roots, in stable traversal order (numeric fixture slot
 * ids index into this order). Pass a PRISTINE list for index-based fixtures (before products are
 * seated) — the caller snapshots it. */
export function collectMeshes(roots) {
	const meshes = [];
	for (const root of (Array.isArray(roots) ? roots : [roots])) {
		if (root) root.traverse((n) => { if (n.isMesh) meshes.push(n); });
	}
	return meshes;
}

// Resolve one slot's mesh identifiers → meshes. Numeric id = index into `meshes`; string id =
// match by material.name or node.name (order-independent).
function meshesForIds(meshes, ids) {
	const out = [];
	for (const id of ids) {
		if (typeof id === "number") { if (meshes[id]) out.push(meshes[id]); continue; }
		for (const m of meshes) {
			const mats = Array.isArray(m.material) ? m.material : [m.material];
			if (m.name === id || mats.some((mm) => mm?.name === id)) out.push(m);
		}
	}
	return out;
}

/** Apply material-library bindings to a fixture's mesh slots (the fixture-side of shell material
 * application, DECISION-053 Slice-2). `meshes` = the fixture's frame meshes (PRISTINE order for
 * numeric slot ids). Builds each slot's material once + box-projects it at real-world scale.
 * Returns the count of meshes re-skinned. */
export async function applyFixtureLibraryMaterials({ meshes, materialSlots, binding, materialLibrary, renderer, anisotropy, tier = null }) {
	const jobs = resolveFixtureSlotBindings(binding, materialSlots);
	if (!jobs.length || !Array.isArray(meshes) || !meshes.length) return 0;
	let applied = 0;
	for (const sb of jobs) {
		const mapSet = resolveMapSet(materialLibrary, sb.materialId, tier);   // ASK-142 §14.2 — the tier's map-set
		if (!mapSet || !resolveAssetKeyUrl(mapSet.baseColor)) continue;   // no library / no CDN → skip
		let material;
		try { material = await buildMaterialFromLibrary(mapSet, sb, { renderer, anisotropy }); }
		catch (err) { console.warn(`[material-apply] build failed for '${sb.materialId}' slot '${sb.slot}':`, err?.message || err); continue; }
		// Preserve the slot's matching identity: slot ids are AUTHORED MATERIAL NAMES, and a
		// nameless replacement would break every SUBSEQUENT re-skin (the ASK-085 part-2 rebind
		// silently matched 0 meshes after the first skin). Named after the slot's first id, all
		// meshes sharing this material keep matching that id on later passes.
		material.name = String(sb.ids.find((i) => typeof i === "string") ?? "");
		const metresPerTile = resolveTilingMeters(mapSet, sb.tilingScale);
		for (const mesh of meshesForIds(meshes, sb.ids)) {
			applyBoxProjectedUVs(mesh, metresPerTile, { withUv1: !!material.aoMap });
			mesh.material = material;
			applied++;
		}
	}
	return applied;
}
